import { useCallback, useEffect, useRef } from 'react';
import { OptimizelyProvider } from '@optimizely/react-sdk';
import { ChatbotProvider, useChatbot, type ChatbotVendor } from '@fff-web/chatbot';
import { useUserContext } from '@fff-web/fff-utilities';
import isBrowser from '../../utils/isBrowser';
import { getOptimizely } from '../../utils/optimizely';
import { useOptimizelyUser } from '../../utils/useOptimizelyUser';
import { type ChatWidgetProps, type DefaultChatWidgetProps } from './ChatWidget.types';
import { fetchChatbotMetadata, flattenUserInfo, isChatPathAllowed } from './ChatWidget.utils';
import { useChatbotVendor } from './useChatbotVendor';

type VendorChatWidgetProps = DefaultChatWidgetProps & { vendor: ChatbotVendor };

const ChatWidgetDefault = ({ hideWidget }: DefaultChatWidgetProps) => {
  // State
  const { userInfo } = useUserContext();
  const { isMounted, isOpen, mount, unmount, updateMetadata } = useChatbot();
  // Identity the live widget was mounted with (`null` when none). It is fixed at mount time (Giga
  // seeds identity only at load), so a change forces a re-mount. The vendor never changes here.
  const mounted = useRef<{ userId?: string } | null>(null);

  // Helpers
  const isPathAllowed = isChatPathAllowed();
  const userId = userInfo.user.userId?.toString();

  // Effects
  /**
   * Manages chatbot mounting state
   */
  useEffect(() => {
    const shouldMount = isBrowser() && isPathAllowed && !hideWidget;
    const identityChanged = !!mounted.current && mounted.current.userId !== userId;

    if (!shouldMount || identityChanged) {
      if (isMounted || mounted.current) unmount();
      mounted.current = null;
      if (!shouldMount) return;
    }

    if ((!isMounted && !mounted.current) || identityChanged) {
      // The BFF metadata is fetched before mounting because Giga reads its context only at load.
      // Cancelled on cleanup, so StrictMode's dev mount→cleanup→mount cycle (whose provider cleanup
      // tears the widget down) mounts once. Mounting there leaves Giga's already-fetched loader
      // running against the cleared config: an empty, invisible widget.
      mounted.current = { userId };
      let cancelled = false;
      let fired = false;
      (async () => {
        const metadata = await fetchChatbotMetadata();
        if (cancelled) return;
        fired = true;
        mount({
          userId,
          metadata: { context: { ...flattenUserInfo(userInfo.user), ...metadata } },
        });
      })();

      return () => {
        cancelled = true;
        if (!fired) mounted.current = null;
      };
    }
  }, [isMounted, hideWidget, mount, unmount, userInfo.user, userId, isPathAllowed]);

  /**
   * Handle chat open event - ALWAYS fetch fresh metadata on every open
   */
  const handleChatOpen = useCallback(async () => {
    const context = await fetchChatbotMetadata();
    updateMetadata({ context });
  }, [updateMetadata]);

  /**
   * Subscribe to chat open event
   */
  useEffect(() => {
    if (!isMounted || !isOpen) return;
    handleChatOpen();
  }, [isMounted, isOpen, handleChatOpen]);

  return null;
};

const VendorChatWidget = ({
  appliedAgentId,
  gigaAgentId,
  vendor,
  ...props
}: VendorChatWidgetProps & { appliedAgentId: string; gigaAgentId?: string }) => {
  const { userInfo } = useUserContext();
  const agentId = vendor === 'giga' ? gigaAgentId : appliedAgentId;

  return (
    <ChatbotProvider agentId={agentId} userId={userInfo.user.userId?.toString()} vendor={vendor}>
      <ChatWidgetDefault {...props} />
    </ChatbotProvider>
  );
};

const FlaggedChatWidget = (props: ChatWidgetProps) => {
  const vendor = useChatbotVendor();
  if (!vendor) return null;

  const { agentId, ...rest } = props;
  return <VendorChatWidget appliedAgentId={agentId} vendor={vendor} {...rest} />;
};

const OptimizelyChatWidget = (props: ChatWidgetProps) => {
  const user = useOptimizelyUser();

  return (
    <OptimizelyProvider optimizely={getOptimizely()} user={user}>
      <FlaggedChatWidget {...props} />
    </OptimizelyProvider>
  );
};

/**
 * Renders the chatbot with Applied Labs, or with Giga when `giga_chatbot_activation` is on. The flag
 * is only consulted when a Giga agent id is configured, so apps without one stay on Applied Labs.
 */
export const ChatWidget = ({ agentId, gigaAgentId, ...props }: ChatWidgetProps) => {
  if (!gigaAgentId) {
    return <VendorChatWidget appliedAgentId={agentId} vendor="applied" {...props} />;
  }

  return <OptimizelyChatWidget agentId={agentId} gigaAgentId={gigaAgentId} {...props} />;
};
